import angular, { IFormController } from 'angular';
import {
    COUNTRIES,
    CartAddress,
    UserModel,
    dirtyfields,
    hasOwnProperty,
} from '../../tn.common';
import {
    StripeNewCreditCard,
    StripeService,
} from '../../tn.stripe/service.tn.stripe';
import { UserService } from '../../tn.user/service.tn.user';

interface SelectedCard {
    card?: string;
}

function userModelToBillingAddress(
    personalInfo: Partial<UserModel> = {}
): CartAddress {
    const name_parts: string[] = [];
    if (personalInfo.first_name) {
        name_parts.push(personalInfo.first_name);
    }
    if (personalInfo.last_name) {
        name_parts.push(personalInfo.last_name);
    }
    const name = name_parts.length > 1 ? name_parts.join(' ') : name_parts[0];

    return {
        first_name: personalInfo.first_name || '',
        last_name: personalInfo.last_name || '',
        address1: personalInfo.address1 || '',
        address2: personalInfo.address2 || '',
        city: personalInfo.city || '',
        company: personalInfo.company || '',
        country: personalInfo.country || '',
        name,
        state: personalInfo.state || '',
        zipcode: personalInfo.zipcode || '',
        vat: personalInfo.vat || '',
    };
}

export class AccountSettingsController {
    user?: UserModel;
    // initialize personalInfo form from user model.
    personalInfo?: UserModel;

    isOpen = false;
    creditCards = this.stripeService.getCards();
    defaultCard = this.stripeService.getDefaultCard();
    billingInfoForm?: IFormController;
    billingAddress = userModelToBillingAddress(this.personalInfo);
    isSubmitted = false;
    passwordForm?: IFormController;
    personalInfoForm?: IFormController;
    personalFormSubmitted = false;
    errorText?: string = undefined;
    selectedCard: SelectedCard = {};
    ccError?;
    cc = new StripeNewCreditCard();

    static $inject = ['tn.user.service', 'tn.stripe.service'];
    constructor(
        protected userService: UserService,
        protected stripeService: StripeService
    ) {
        userService.user$.subscribe((user) => {
            this.user = user;
            this.personalInfo = Object.assign({}, user);
            this.billingAddress = userModelToBillingAddress(this.personalInfo);
            this.creditCards = this.stripeService.getCards();
            this.defaultCard = this.stripeService.getDefaultCard();
        });
        userService.logout$.subscribe(() => {
            this.creditCards = [];
            this.defaultCard = null;
        });
    }

    updateCreditCards(): void {
        delete this.ccError;
        this.isSubmitted = true;
        if (this.selectedCard.card === 'new' || !this.creditCards.length) {
            if (this.newCardIsValid()) {
                this.stripeService
                    .createTokenForCard(this.cc, this.billingAddress)
                    .then((token) => {
                        this.stripeService
                            .saveCard(token, this.billingAddress.vat)
                            .then((res) => {
                                if (this.cc.default) {
                                    this.stripeService.setDefaultCard(res.id);
                                }
                                this.stripeService
                                    .getCardsFromStripe()
                                    .then(() => {
                                        this.selectedCard.card =
                                            this.defaultCard?.default;
                                    });
                                // reset CC form after submission.
                                this.isSubmitted = false;
                                this.cc = new StripeNewCreditCard();
                                this.billingAddress = userModelToBillingAddress(
                                    this.personalInfo
                                );
                            })
                            .catch((err) => {
                                if (err.data && err.data.error) {
                                    this.ccError = err.data.error.message;
                                } else {
                                    this.ccError = err;
                                }
                                this.isSubmitted = false;
                            });
                    })
                    .catch((err) => {
                        if (err.error) {
                            if (
                                err.error.type &&
                                err.error.type === 'card_error'
                            ) {
                                this.ccError = err.error.message;
                            } else {
                                this.ccError = err.error;
                            }
                        }
                        this.isSubmitted = false;
                    });
            } else {
                this.billingInfoForm && dirtyfields(this.billingInfoForm);
                this.isSubmitted = false;
            }
        } else {
            this.selectedCard.card &&
                this.stripeService
                    .setDefaultCard(this.selectedCard.card)
                    .then(() => {
                        this.isSubmitted = false;
                    });
        }
    }

    newCardIsValid(): boolean {
        // has proper credit card and billing information
        return !!(
            this.cc &&
            this.cc.number &&
            this.cc.isValid &&
            this.cc.CVC &&
            this.billingInfoForm?.$valid
        );
    }

    updatePassword(newPassword: string): void {
        const updatedUser = angular.extend(this.user, {
            password: newPassword,
        });
        this._saveUserAndUpdatePersonalInfo(updatedUser);
        this.passwordForm?.$setPristine();
        this.passwordForm?.$setSubmitted();
    }

    updateAccount(): void {
        if (this.personalInfoFormIsValid() && this.personalInfo) {
            this.userService
                .saveUser(this.personalInfo)
                .then(() => {
                    this.errorText = undefined;
                    this.isOpen = false;
                    this.billingAddress = userModelToBillingAddress(
                        this.personalInfo
                    );
                })
                .catch((error) => {
                    if (hasOwnProperty(error, 'email')) {
                        this.errorText = error.email[0];
                    } else {
                        this.errorText = 'An unexpected error has occured.';
                    }
                });
        } else {
            this.personalInfoForm && dirtyfields(this.personalInfoForm);
        }
    }

    getFullCountryName(abbreviation: string): string | null {
        const countryName = COUNTRIES.filter(
            (country) => country.value === abbreviation
        );
        if (
            typeof countryName === 'undefined' ||
            typeof countryName[0] === 'undefined'
        ) {
            return null;
        }
        return countryName[0].label;
    }

    _saveUserAndUpdatePersonalInfo(updatedUser: UserModel): void {
        this.userService.saveUser(updatedUser).then((user) => {
            this.personalInfo = { ...user };
            this.billingAddress = userModelToBillingAddress(this.personalInfo);
            this.personalFormSubmitted = true;
        });
    }

    personalInfoFormIsValid(): boolean {
        // Check to make sure the form is populated, valid, and that there is a valid email in it
        return (
            this.personalInfoForm?.$valid &&
            this.personalInfoForm?.email?.$valid &&
            this.personalInfoForm?.email?.$modelValue !== ''
        );
    }
}
